/**
 * ctrLpBuyBox — Alpine component for sections/ctr-lp-buy-box.liquid.
 *
 * Mirrors the cart logic of snippets/bundle-builder-block.liquid (tiers, Skio
 * dynamic box, selling plans, direct checkout, shipping threshold message,
 * presets and the complete-stack upsell) with prices read from live variants.
 */
document.addEventListener('alpine:init', () => {
  const BUILT_IN_PRESETS = {
    'variety-pack': {
      '47179793072306': 1, // Blue Razz
      '46882444411058': 1, // Tropical
      '46737691771058': 1, // Watermelon
    },
  };

  function parsePairs(str) {
    return String(str || '')
      .split(',')
      .map((pair) => pair.trim().split(':'))
      .filter((parts) => parts.length === 2)
      .map((parts) => [parseInt(parts[0], 10), parseFloat(parts[1])])
      .filter((pair) => !Number.isNaN(pair[0]) && !Number.isNaN(pair[1]))
      .sort((a, b) => a[0] - b[0]);
  }

  function parseLines(str) {
    return String(str || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const idx = line.indexOf(':');
        return [parseInt(line.substring(0, idx), 10), line.substring(idx + 1)];
      })
      .filter((pair) => !Number.isNaN(pair[0]));
  }

  function parseSellingPlans(str) {
    return parseLines(str).map(([minQty, rest]) => {
      const commaIdx = rest.indexOf(',');
      return {
        minQty: minQty,
        planId: parseInt(commaIdx === -1 ? rest : rest.substring(0, commaIdx), 10),
        label: commaIdx === -1 ? '' : rest.substring(commaIdx + 1).trim(),
      };
    });
  }

  function queryParamMatches(queryParam) {
    if (!queryParam) return true;
    const expected = new URLSearchParams(queryParam);
    const actual = new URLSearchParams(window.location.search);
    return Array.from(expected.keys()).every((key) => actual.get(key) === expected.get(key));
  }

  Alpine.data('ctrLpBuyBox', () => ({
    config: null,
    tiers: [],
    tiersRecurring: [],
    ctaTiers: [],
    sellingPlans: [],
    selections: {},
    // The Buy Box's own flavors, fixed at init: an accepted upsell flavor is
    // pushed onto config.flavors but is added to the cart, not synced.
    flavorIds: [],
    selectionVersion: 0,
    // The cart as read for Pre-selection.
    cart: null,
    isLoading: false,
    error: '',
    modal: null,
    gallerySwiper: null,
    thumbsSwiper: null,
    swiperRetries: 0,

    init() {
      const configEl = this.$el.querySelector('[data-ctr-lp-buy-box-config]');
      try {
        this.config = JSON.parse(configEl ? configEl.textContent : '{}');
      } catch (e) {
        this.config = {};
      }
      this.config.flavors = this.config.flavors || [];
      this.config.texts = this.config.texts || {};

      this.tiers = parsePairs(this.config.tiers);
      this.tiersRecurring = parsePairs(this.config.tiersRecurring);
      this.ctaTiers = parseLines(this.config.texts.ctaTiers);
      this.sellingPlans = parseSellingPlans(this.config.sellingPlans);

      this.flavorIds = this.config.flavors.map((f) => String(f.variantId));
      this.applyPresets(this.getInitialPresets());
      this.preselectFromCart();
      this.initGallery();

      window.addEventListener('pageshow', (e) => {
        if (!e.persisted) return;
        this.isLoading = false;
        this.error = '';
        this.preselectFromCart();
      });

      window.bundleBuilderSetPresets = (presets, queryParam) => {
        if (!queryParamMatches(queryParam)) return;
        this.applyPresets(presets);
      };

      // Going to checkout leaves the CTA in its loading state. When the user hits Back,
      // the browser restores this page from the back/forward cache as-is, so reset it.
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.isLoading = false;
      });
    },

    destroy() {
      if (this.thumbsSwiper) this.thumbsSwiper.destroy(true, true);
      if (this.gallerySwiper) this.gallerySwiper.destroy(true, true);
    },

    /* --- Gallery ----------------------------------------------------------- */

    initGallery() {
      if (!this.$refs.gallery) return;
      if (typeof Swiper === 'undefined') {
        if (this.swiperRetries >= 40) return;
        this.swiperRetries += 1;
        window.setTimeout(() => this.initGallery(), 50);
        return;
      }

      if (this.$refs.thumbs) {
        this.thumbsSwiper = new Swiper(this.$refs.thumbs, {
          slidesPerView: 5,
          spaceBetween: 24,
          watchSlidesProgress: true,
          breakpoints: { 0: { spaceBetween: 10 }, 750: { spaceBetween: 24 } },
        });
      }

      this.gallerySwiper = new Swiper(this.$refs.gallery, {
        slidesPerView: 1.15,
        spaceBetween: 10,
        grabCursor: true,
        thumbs: this.thumbsSwiper ? { swiper: this.thumbsSwiper } : undefined,
        breakpoints: { 750: { slidesPerView: 1, spaceBetween: 0 } },
        on: {
          init: (swiper) => this.syncGalleryVideos(swiper),
          slideChange: (swiper) => this.syncGalleryVideos(swiper),
        },
      });
    },

    // Same behavior as assets/pdp-swiper-gallery.js: play the active slide's video, pause the rest.
    syncGalleryVideos(swiper) {
      swiper.slides.forEach((slide, index) => {
        const video = slide.querySelector('video');
        if (!video) return;
        if (index === swiper.activeIndex) {
          video.muted = true;
          video.playsInline = true;
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    },

    /* --- Selections -------------------------------------------------------- */

    getInitialPresets() {
      if (queryParamMatches(window.bundleBuilderPresetsQuery) && window.bundleBuilderPresets) {
        return window.bundleBuilderPresets;
      }
      const bb = new URLSearchParams(window.location.search).get('bb');
      if (bb && BUILT_IN_PRESETS[bb]) return BUILT_IN_PRESETS[bb];

      const defaults = {};
      this.config.flavors.forEach((f) => {
        if (f.defaultQty > 0 && f.available) defaults[f.variantId] = f.defaultQty;
      });
      return defaults;
    },

    // Pre-selection: show the cart's cans of the Buy Box's flavors, or the
    // initial presets when the cart has none. Skipped if the shopper changed the
    // selection while the cart was loading, and on the cart-drawer path, which
    // adds the selection on top of the cart instead of syncing it.
    preselectFromCart() {
      if (!this.isDirectToCheckout) return;
      if (!window.CtrCartSync) {
        window.addEventListener('load', () => this.preselectFromCart(), { once: true });
        return;
      }
      const version = this.selectionVersion;
      window.CtrCartSync.fetchCart()
        .then((cart) => {
          this.cart = cart;
          if (this.selectionVersion !== version) return;
          const preselection = window.CtrCartSync.readPreselection(cart, this.flavorIds);
          this.applyPresets(preselection.total > 0 ? preselection.selections : this.getInitialPresets());
        })
        .catch(() => {});
    },

    applyPresets(presets) {
      const next = {};
      this.config.flavors.forEach((f) => {
        next[f.variantId] = (presets && parseInt(presets[f.variantId], 10)) || 0;
      });
      this.selections = next;
    },

    qty(variantId) {
      return this.selections[variantId] || 0;
    },

    increment(variantId) {
      if (this.atMaxCans || this.isLoading) return;
      this.selections[variantId] = this.qty(variantId) + 1;
      this.selectionVersion += 1;
      this.error = '';
    },

    decrement(variantId) {
      if (this.qty(variantId) === 0 || this.isLoading) return;
      this.selections[variantId] = this.qty(variantId) - 1;
      this.selectionVersion += 1;
    },

    get totalQty() {
      return Object.values(this.selections).reduce((sum, q) => sum + q, 0);
    },

    get minQty() {
      return this.tiers.length ? this.tiers[0][0] : 1;
    },

    get isValid() {
      return this.totalQty >= this.minQty;
    },

    get atMaxCans() {
      return Boolean(this.config.maxCansEnabled) && this.totalQty >= this.config.maxCansLimit;
    },

    /* --- Pricing ----------------------------------------------------------- */

    discountFor(qty, tiers) {
      for (let i = tiers.length - 1; i >= 0; i--) {
        if (qty >= tiers[i][0]) return tiers[i][1];
      }
      return 0;
    },

    get discount() {
      return this.discountFor(this.totalQty, this.tiers);
    },

    get originalCents() {
      return this.config.flavors.reduce((sum, f) => sum + this.qty(f.variantId) * f.price, 0);
    },

    get savingsCents() {
      return Math.round(this.originalCents * (this.discount / 100));
    },

    get finalCents() {
      return this.originalCents - this.savingsCents;
    },

    get nextTier() {
      return this.tiers.find((tier) => this.totalQty < tier[0]) || null;
    },

    get offerLabel() {
      const texts = this.config.texts;
      if (!this.isValid && this.nextTier) {
        return (texts.offerNext || '').replace('[count]', this.nextTier[0] - this.totalQty);
      }
      if (!this.nextTier) return texts.offerMax || texts.offerActive || '';
      return texts.offerActive || '';
    },

    get offerBadge() {
      const pct = this.isValid ? this.discount : this.nextTier ? this.nextTier[1] : 0;
      return (this.config.texts.offerBadge || '').replace('[discount]', pct);
    },

    get ctaText() {
      let text = this.ctaTiers.length ? this.ctaTiers[0][1] : '';
      for (let i = this.ctaTiers.length - 1; i >= 0; i--) {
        if (this.totalQty >= this.ctaTiers[i][0]) {
          text = this.ctaTiers[i][1];
          break;
        }
      }
      return text
        .replace('[price]', this.money(this.finalCents))
        .replace('[remaining]', Math.max(0, this.minQty - this.totalQty));
    },

    get otpCtaText() {
      const label = this.config.texts.otpCta || '';
      return this.isValid
        ? label.replace('[price]', this.money(this.originalCents))
        : label.replace('[price]', '').trim();
    },

    money(cents) {
      const currency = this.$root.dataset.currency || 'USD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency',
          currency: currency,
        }).format((cents || 0) / 100);
      } catch (e) {
        return '$' + ((cents || 0) / 100).toFixed(2);
      }
    },

    /* --- Benefits list (bundle-builder__benefits on the PDP) -------------- */

    get recurringSavingsCents() {
      return Math.round(this.originalCents * (this.discountFor(this.totalQty, this.tiersRecurring) / 100));
    },

    get selectedPlanLabel() {
      const plan = this.sellingPlans.find((p) => p.planId === this.selectedPlanId);
      return plan ? plan.label : '';
    },

    get benefitLines() {
      // Same $50 free-shipping cutoff as the PDP bundle builder.
      const shipping = this.finalCents >= 5000 ? 'Fast Free Same-Day Shipping' : 'Fast Same-Day Shipping';
      return String(this.config.texts.benefits || '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) =>
          line
            .replace('[shipping]', shipping)
            .replace('[savings]', this.money(this.recurringSavingsCents))
            .replace('[qty]', this.totalQty)
            .replace('[frequency]', this.selectedPlanLabel)
        );
    },

    /* --- Shipping threshold message ---------------------------------------- */

    shippingFor(qty) {
      const stn = this.config.shippingThreshold;
      if (!stn || !stn.enabled || qty < stn.thresholdQty) return null;
      const unlocked = qty >= stn.unlockedQty;
      const remaining = Math.max(0, stn.unlockedQty - qty);
      const color = unlocked ? stn.unlockedColor : stn.thresholdColor;
      let message = (unlocked ? stn.unlockedMessage : stn.thresholdMessage).replace(/\[value\]/g, remaining);
      if (remaining >= 2) message = message.replace(/\bmore can\b/gi, 'more cans');
      message = message
        .replace(/[<>]/g, '')
        .replace(/\*(.+?)\*/g, '<span style="color:' + color + '">$1</span>');
      return {
        color: color,
        messageHtml: message,
        counter: Math.min(qty, stn.unlockedQty) + '/' + stn.unlockedQty + ' selected',
      };
    },

    get shipping() {
      return this.shippingFor(this.totalQty);
    },

    /* --- Cart -------------------------------------------------------------- */

    get selectedPlanId() {
      let planId = this.sellingPlans.length ? this.sellingPlans[0].planId : null;
      for (let i = this.sellingPlans.length - 1; i >= 0; i--) {
        if (this.totalQty >= this.sellingPlans[i].minQty) {
          planId = this.sellingPlans[i].planId;
          break;
        }
      }
      return planId;
    },

    get isDirectToCheckout() {
      return this.config.addToCartBehavior !== 'cart_drawer';
    },

    buildCartItems(oneTime) {
      const items = [];
      Object.keys(this.selections).forEach((variantId) => {
        for (let i = 0; i < this.selections[variantId]; i++) {
          const item = { id: parseInt(variantId, 10), quantity: 1 };
          if (!oneTime) {
            item.properties = { _dynamicBoxIndex: 0, _dynamicBoxId: this.config.dynamicBoxId };
            if (this.selectedPlanId) item.selling_plan = this.selectedPlanId;
          }
          items.push(item);
        }
      });
      return items;
    },

    async postJSON(url, body) {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status === 422) {
        throw new Error(data.description || data.message || this.config.texts.error);
      }
      return data;
    },

    async submit(oneTime) {
      if (!this.isValid || this.isLoading) return false;
      this.isLoading = true;
      this.error = '';
      const routes = window.routes || {};

      try {
        if (this.isDirectToCheckout) {
          const purchase = oneTime ? null : { sellingPlanId: this.selectedPlanId, boxId: this.config.dynamicBoxId };
          const result = await window.CtrCartSync.sync(this.selections, this.flavorIds, purchase);
          if (result.overLimit) {
            await window.CtrCartSync.openCartDrawerWithLimitError();
            this.isLoading = false;
            return false;
          }
        } else {
          await this.postJSON(routes.cart_add_url || '/cart/add.js', { items: this.buildCartItems(oneTime) });
        }
        if (this.config.source) {
          await this.postJSON('/cart/update.js', { attributes: { buy_box_source: this.config.source } });
        }

        if (this.isDirectToCheckout) {
          window.location.href = '/checkout';
          return true;
        }

        this.applyPresets({});
        this.refreshCartDrawer();
        this.isLoading = false;
        return true;
      } catch (error) {
        this.error = error.message || this.config.texts.error;
        this.isLoading = false;
        return false;
      }
    },

    refreshCartDrawer() {
      const routes = window.routes || {};
      const cartUrl = routes.cart_url || '/cart';

      fetch(cartUrl + '?section_id=cart-drawer')
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newDrawer = doc.querySelector('#CartDrawer');
          const currentDrawer = document.querySelector('#CartDrawer');
          if (newDrawer && currentDrawer) currentDrawer.replaceWith(newDrawer);
          const drawer = document.querySelector('cart-drawer');
          if (!drawer) return;
          drawer.classList.remove('is-empty');
          drawer.classList.add('active');
          document.body.classList.add('overflow-hidden');
          const overlay = document.querySelector('#CartDrawer-Overlay');
          if (overlay) {
            overlay.addEventListener('click', () => {
              drawer.classList.remove('active');
              document.body.classList.remove('overflow-hidden');
            });
          }
        })
        .catch(() => {
          window.location.href = cartUrl;
        });

      if (window.ctrRefreshCart) window.ctrRefreshCart();
    },

    /* --- Complete-stack upsell (snippets/ctr-complete-stack) ---------------- */

    shouldShowUpsell() {
      const upsell = this.config.upsell;
      if (!upsell || !upsell.enabled || !this.isDirectToCheckout) return false;
      const upsellIds = (upsell.flavors || []).map((f) => f.variantId);
      return !window.CtrCartSync.skipUpsell(this.cart, this.selections, this.flavorIds, upsellIds);
    },

    openUpsell(oneTime) {
      const upsell = this.config.upsell;
      document.dispatchEvent(
        new CustomEvent('ctr-complete-stack:open', {
          detail: {
            config: upsell,
            quote: (variantId) => {
              const flavor = (upsell.flavors || []).find((f) => f.variantId === variantId);
              if (!flavor) return null;
              const qty = this.totalQty + 1;
              const original = this.originalCents / 100 + flavor.price;
              const discount = oneTime ? 0 : this.discountFor(qty, this.tiers);
              const shipping = this.shippingFor(qty);
              return {
                original: original,
                final: original - original * (discount / 100),
                shipping: shipping && {
                  color: shipping.color,
                  iconSvg: '',
                  messageHtml: shipping.messageHtml,
                  counter: shipping.counter,
                },
              };
            },
            onAccept: async (variantId) => {
              const flavor = (upsell.flavors || []).find((f) => f.variantId === variantId);
              if (!flavor) return false;
              const prev = this.qty(variantId);
              if (!this.config.flavors.some((f) => f.variantId === variantId)) {
                this.config.flavors.push({ variantId: variantId, price: Math.round(flavor.price * 100), available: true });
              }
              this.selections[variantId] = prev + 1;
              const ok = await this.submit(oneTime);
              if (!ok) this.selections[variantId] = prev;
              return ok;
            },
            onDecline: () => this.submit(oneTime),
          },
        })
      );
    },

    handleCta(oneTime) {
      if (!this.isValid) {
        this.error = '';
        return;
      }
      if (this.shouldShowUpsell()) {
        this.openUpsell(oneTime);
        return;
      }
      this.submit(oneTime);
    },

    /* --- Flavor detail modal ----------------------------------------------- */

    openModal(id) {
      this.modal = id;
      document.body.classList.add('overflow-hidden');
    },

    closeModal() {
      if (!this.modal) return;
      this.modal = null;
      document.body.classList.remove('overflow-hidden');
    },
  }));
});
